import { styled } from '@mui/system';
import { Box, Typography } from '@mui/material';
import { colors } from '../../constants/theme';
import { figmaLetterSpacing } from '../../ui/utils';

export const ActionCardWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction = 'column', theme }) => ({
  display: 'flex',
  flexDirection: direction,
  gap: 10,
  padding: 10,
  margin: 10,
  borderRadius: 40,
  backgroundColor: '#FFFFFF',
  maxWidth: 1100,
  width: '100%',
  boxShadow: `
  -4px -4px 17.8px 0px rgba(205, 214, 223, 0.15),
   4px  4px 15px   0px rgba(205, 214, 223, 0.25)
`,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    borderRadius: 24,
    padding: 8,
    margin: 8,
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
    padding: 6,
    margin: 6,
  },
}));

export const ActionCardImageWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction = 'column', theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: direction === 'row' ? '45.45%' : '100%',
  borderRadius: 30,
  backgroundColor: colors.iconBackground,
  padding: 55,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    borderRadius: 20,
    padding: 40,
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: 16,
    padding: 30,
  },
}));

export const ActionCardImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'rounded',
})(({ rounded = true }) => ({
  width: 'initial',
  // Fixed-width artwork (the Nirva lockup is 391px) would otherwise spill out
  // of the tile once the padding eats the available room on narrow screens.
  maxWidth: '100%',
  display: 'block',
  objectFit: 'contain',
  clipPath: rounded ? 'circle(46.4% at 49.6% 50%)' : 'none',
}));

export const ActionCardInfoContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction = 'column', theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: direction === 'row' ? 'flex-start' : 'center',
  width: direction === 'row' ? '54.55%' : '100%',
  gap: 50,
  padding: 55,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    alignItems: 'center',
    gap: 40,
    padding: 40,
  },
  [theme.breakpoints.down('sm')]: {
    gap: 30,
    padding: 30,
  },
}));

export const ActionCardTextWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction = 'column', theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: direction === 'row' ? 'flex-start' : 'center',
  gap: 20,
  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    gap: 16,
  },
  [theme.breakpoints.down('sm')]: {
    gap: 14,
  },
}));

export const ActionCardTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.heading,
  fontSize: 60,
  lineHeight: '77px',
  textTransform: 'capitalize',
  [theme.breakpoints.down('md')]: {
    fontSize: 42,
    lineHeight: '52px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 32,
    lineHeight: '40px',
  },
}));

export const ActionCardSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 500,
  fontSize: 24,
  lineHeight: '100%',
  letterSpacing: figmaLetterSpacing(24, -1),
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  [theme.breakpoints.down('md')]: {
    fontSize: 20,
    letterSpacing: figmaLetterSpacing(20, -1),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
    letterSpacing: figmaLetterSpacing(18, -1),
  },
}));

export const ActionCardDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction = 'column', theme }) => ({
  ...theme.typography.body1,
  fontSize: 24,
  lineHeight: 'normal',
  letterSpacing: figmaLetterSpacing(24, 3),
  textAlign: direction === 'row' ? 'left' : 'center',
  maxWidth: direction === 'row' ? '460px' : '100%',
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    maxWidth: '100%',
    fontSize: 20,
    letterSpacing: figmaLetterSpacing(20, 3),
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
    letterSpacing: figmaLetterSpacing(18, 3),
  },
  '& b': {
    fontWeight: 600,
  },
}));
